import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { RefreshCw, Table2, Filter, ArrowUpDown, Search, Code2, Pencil, Plus, Save, Undo2, FileDown, BarChart2, BarChart3, Copy, Check, LayoutDashboard, PanelRightClose, PanelRightOpen, Map, Columns3 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { ReasonButton } from '../../../../components/ui/ReasonButton';
import { Input } from '../../../../components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../../components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import { ColumnSelector } from './ColumnSelector';
import { WorkspacePinButton } from '../../common/components/WorkspacePinButton';
import { SkipIdentityToggleRow } from '../../common/components/SkipIdentityToggleRow';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import { normalizeColumnName } from '../../common/utils/normalizeColumnName';
import { getQueryPreviewButtonState } from '../utils/queryPreviewUtils';
import type { ResultsExportFormat } from '../../../../types/resultsExport';

export type CopyFormat = 'csv' | 'markdown' | 'insert' | 'html';
export type TableExplorerSortMode = 'server' | 'loaded-page' | 'none';

/** The Export menu's rows, in GridExportCopyMenu's order (Excel leads). */
const EXPORT_MENU_ITEMS: ReadonlyArray<{ format: ResultsExportFormat; label: string; extension: string }> = [
  { format: 'xlsx', label: 'Excel', extension: '.xlsx' },
  { format: 'csv', label: 'CSV', extension: '.csv' },
  { format: 'json', label: 'JSON', extension: '.json' },
  { format: 'markdown', label: 'Markdown', extension: '.md' },
  { format: 'sql', label: 'SQL (INSERT)', extension: '.sql' },
];

type TableExplorerToolbarProps = {
  /** Owning tab id — wires the pin button to the pin-capture registry (Task 19). */
  tabId: string;
  /**
   * Row (record-details) context. When true the pin button is disabled with a
   * "navigate to a table view to pin" reason — row views aren't capturable (G7).
   */
  isRowContext?: boolean;
  tableName: string;
  schema: string;
  isLoading: boolean;
  isFetching: boolean;
  onRefresh: () => void;
  onPreviewQuery: () => void;
  supportsQueryPreview: boolean;
  filterCount: number;
  sortCount: number;
  columns: TableExplorerColumn[];
  selectedColumns: string[] | '*';
  onSelectedColumnsChange: (columns: string[] | '*') => void;
  onAddFilter: (columnName: string) => void;
  onAddSort: (columnName: string) => void;
  copyFormats?: readonly CopyFormat[];
  exportFormats?: readonly ResultsExportFormat[];
  sortMode?: TableExplorerSortMode;
  /** When true, the Add Filter picker is gated — clicking it fires
   * `onAddFilterBlocked` instead of opening the column picker. Set when the
   * filter wizard has a dirty draft pending. */
  addFilterBlocked?: boolean;
  onAddFilterBlocked?: () => void;
  // Columns that already have filters/sorts (to disable in selector)
  filteredColumns: string[];
  sortedColumns: string[];
  // Edit mode props
  isEditMode?: boolean;
  onToggleEditMode?: () => void;
  pendingChangeCount?: number;
  onAddRow?: () => void;
  onSaveChanges?: () => void;
  onDiscardChanges?: () => void;
  /** Whether the table has a primary key (required for edit mode) */
  hasPrimaryKey?: boolean;
  /**
   * When set, the Enable Edit Mode toggle is disabled and this string is shown as its tooltip.
   * Currently used to lock editing while the page is in windowed (streaming-from-cache) mode,
   * since rows aren't fully materialized in the renderer.
   */
  editDisabledReason?: string;
  /**
   * Slug-form of `editDisabledReason` exposed via `data-disabled-reason` on
   * the Edit Mode button. Lets E2E assert WHY the button is disabled without
   * coupling to the human-readable tooltip text. `'read-only-engine'` marks
   * an append-only engine (e.g. Kusto/ADX) where editing is never available,
   * regardless of primary-key/windowed-mode state.
   */
  editDisabledKey?: 'windowed' | 'read-only-engine' | 'storage-read-only';
  /** Export current grid data (renderer supplies the data to export). */
  onExport?: (format: ResultsExportFormat) => void;
  canExport?: boolean;
  isExporting?: boolean;
  /** Open chart dialog to visualize current data */
  onChart?: () => void;
  /** S20 — pin the current table SELECT to a dashboard. */
  onPinToDashboard?: () => void;
  canChart?: boolean;
  /** Toggle the row side panel (right) */
  onToggleRowSidePanel?: () => void;
  isRowSidePanelOpen?: boolean;
  canOpenRowSidePanel?: boolean;
  /** Toggle the spatial map pane (right) — rendered only when the table has ≥1 spatial column */
  onToggleMapPane?: () => void;
  isMapPaneOpen?: boolean;
  hasSpatialColumn?: boolean;
  /** Copy data to clipboard */
  onCopy?: (format: CopyFormat) => void;
  canCopy?: boolean;
  isCopied?: boolean;
  /** Column profiling toggle */
  showColumnProfiling?: boolean;
  onToggleColumnProfiling?: () => void;
  canColumnProfiling?: boolean;
  /**
   * Blueprint-edit mode. When true the grid is backed by a Database Blueprint
   * `.data.json` sidecar (not a live DB), so live-DB-only actions are hidden
   * (Preview SQL, Refresh from DB) and Save is relabelled to make clear edits
   * write to the blueprint files, not the database.
   */
  blueprintMode?: boolean;
  /**
   * C2 N9: the host cannot read the rows at all (an Azure Table refused for a missing data role — the tab shows the
   * refusal instead of a grid). Add Filter, Sort and Columns then stay unavailable WITH this reason instead of offering
   * pickers over rows that cannot be read. SQL hosts never set it.
   */
  readUnavailableReason?: string;
};

export function TableExplorerToolbar({
  tabId,
  isRowContext = false,
  tableName,
  schema,
  isLoading,
  isFetching,
  onRefresh,
  onPreviewQuery,
  supportsQueryPreview,
  filterCount,
  sortCount,
  columns,
  selectedColumns,
  onSelectedColumnsChange,
  onAddFilter,
  onAddSort,
  copyFormats = ['csv', 'markdown', 'insert', 'html'],
  exportFormats = ['xlsx', 'csv', 'json', 'markdown', 'sql'],
  sortMode = 'server',
  addFilterBlocked = false,
  onAddFilterBlocked,
  filteredColumns,
  sortedColumns,
  isEditMode = false,
  onToggleEditMode,
  pendingChangeCount = 0,
  onAddRow,
  onSaveChanges,
  onDiscardChanges,
  hasPrimaryKey = false,
  editDisabledReason,
  editDisabledKey,
  onExport,
  canExport = true,
  isExporting = false,
  onChart,
  onPinToDashboard,
  canChart = true,
  onToggleRowSidePanel,
  isRowSidePanelOpen = false,
  canOpenRowSidePanel = true,
  onToggleMapPane,
  isMapPaneOpen = false,
  hasSpatialColumn = false,
  onCopy,
  canCopy = true,
  isCopied = false,
  showColumnProfiling = false,
  onToggleColumnProfiling,
  canColumnProfiling = true,
  blueprintMode = false,
  readUnavailableReason,
}: TableExplorerToolbarProps) {
  const fullTableName = schema ? `${schema}.${tableName}` : tableName;
  const previewButtonState = useMemo(
    () => getQueryPreviewButtonState({ supportsQueryPreview, isLoading }),
    [supportsQueryPreview, isLoading]
  );
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isCopyMenuOpen, setIsCopyMenuOpen] = useState(false);

  return (
    // CH-25: `select-none` here covers the toolbar's action controls (add
    // filter/sort, pin, column selector, preview/copy/export dropdowns). The
    // table-name span below is the one piece of real data this bar renders,
    // so it opts back into selection with `select-text` — `user-select` is
    // inherited, so that override is what actually restores it.
    <div className="flex flex-wrap items-center gap-2 px-1 py-1.5 border-b border-border bg-muted/30 min-h-10 shrink-0 select-none">
      {/* Table name display */}
      <div className="flex items-center gap-2 text-sm font-medium">
        <Table2 className="h-4 w-4 text-muted-foreground" />
        <span
          className="text-foreground select-text"
          data-testid="table-explorer-toolbar-table-name"
        >
          {fullTableName}
        </span>
      </div>

      {/* Inline Add Filter button (only when no filters) */}
      {filterCount === 0 && (
        <AddColumnButton
          type="filter"
          columns={columns}
          disabledColumns={filteredColumns}
          onSelect={onAddFilter}
          disabled={isLoading || columns.length === 0}
          blocked={addFilterBlocked}
          onBlockedClick={onAddFilterBlocked}
          reason={readUnavailableReason}
        />
      )}

      {/* Inline Add Sort button (only when no sorts) */}
      {sortCount === 0 && sortMode !== 'none' && (
        <AddColumnButton
          type="sort"
          label={sortMode === 'loaded-page' ? 'Sort loaded page' : undefined}
          columns={columns}
          disabledColumns={sortedColumns}
          onSelect={onAddSort}
          disabled={isLoading || columns.length === 0}
          reason={readUnavailableReason}
        />
      )}

      {/* Pin this workspace (Task 19). Disabled — with an explaining tooltip —
          in blueprint-edit mode and in row/record context (G7). The button's
          own analytics + create/unpin/update logic live in WorkspacePinButton
          (Task 18); it reads this tab's live definition via the pin-capture
          registry. */}
      <WorkspacePinButton
        tabId={tabId}
        disabledReason={
          blueprintMode
            ? "Blueprint editing tabs can't be pinned"
            : isRowContext
              ? 'Navigate to a table view to pin'
              : undefined
        }
      />

      {/* Spacer */}
      <div className="flex-1" />

      {/* Column selector */}
      {readUnavailableReason ? (
        <ReasonButton variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" reason={readUnavailableReason} data-testid="column-selector-button">
          <Columns3 className="h-3.5 w-3.5" />
          <span>Columns</span>
        </ReasonButton>
      ) : (
        <ColumnSelector
          columns={columns}
          selectedColumns={selectedColumns}
          onSelectedColumnsChange={onSelectedColumnsChange}
          disabled={isLoading}
        />
      )}

      {/* Preview Query button — live-DB only; hidden in blueprint-edit mode. */}
      {!blueprintMode && previewButtonState.isVisible && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onPreviewQuery}
          disabled={!previewButtonState.isEnabled}
          className="h-7 px-2"
          title="Preview SQL query"
          data-testid="preview-query-button"
        >
          <Code2 className="h-4 w-4" />
        </Button>
      )}

      {/* Copy to clipboard menu — UI/UX batch C2 R13: real menu items (arrow keys, Enter, Escape), the same as
          GridExportCopyMenu on the Query Editor results grid (two-grid parity). */}
      {onCopy && (
        <DropdownMenu open={isCopyMenuOpen} onOpenChange={setIsCopyMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              disabled={isLoading || !canCopy}
              title={canCopy ? 'Copy to clipboard' : 'No data to copy'}
              data-testid="table-explorer-copy-button"
            >
              {isCopied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44" data-testid="table-explorer-copy-menu">
            {copyFormats.includes('csv') && <DropdownMenuItem disabled={isLoading || !canCopy} onSelect={() => onCopy('csv')} data-testid="table-explorer-copy-csv">CSV</DropdownMenuItem>}
            {copyFormats.includes('markdown') && <DropdownMenuItem disabled={isLoading || !canCopy} onSelect={() => onCopy('markdown')} data-testid="table-explorer-copy-markdown">Markdown</DropdownMenuItem>}
            {copyFormats.includes('insert') && <DropdownMenuItem disabled={isLoading || !canCopy} onSelect={() => onCopy('insert')} data-testid="table-explorer-copy-insert">INSERT</DropdownMenuItem>}
            {copyFormats.includes('html') && <DropdownMenuItem disabled={isLoading || !canCopy} onSelect={() => onCopy('html')} data-testid="table-explorer-copy-html">HTML</DropdownMenuItem>}
            {/* INSERT generation option — persisted, shared with Export SQL
                and with the Query Editor results grid (D3 grid parity). */}
            {copyFormats.includes('insert') && <>
              <DropdownMenuSeparator />
              <SkipIdentityToggleRow testId="table-explorer-copy-skip-identity-toggle" />
            </>}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Export menu — its own glyph (N27), like GridExportCopyMenu. */}
      {onExport && (
        <DropdownMenu open={isExportMenuOpen} onOpenChange={setIsExportMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              disabled={isLoading || isExporting || !canExport}
              title={canExport ? 'Export rows' : 'No rows to export'}
              data-testid="table-explorer-export-button"
            >
              <FileDown className={`h-4 w-4 ${isExporting ? 'animate-pulse' : ''}`} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44" data-testid="table-explorer-export-menu">
            {/* Excel leads — most-used export target (§3d-2). Keep in sync
                with GridExportCopyMenu's export list (grid parity). */}
            {EXPORT_MENU_ITEMS.filter((item) => exportFormats.includes(item.format)).map((item) => (
              <DropdownMenuItem
                key={item.format}
                disabled={isLoading || isExporting || !canExport}
                onSelect={() => onExport(item.format)}
                data-testid={`table-explorer-export-${item.format}`}
              >
                <span>{item.label}</span>
                <span className="ml-auto text-xs text-muted-foreground">{item.extension}</span>
              </DropdownMenuItem>
            ))}
            {/* Same persisted INSERT option as the copy menu (Export SQL). */}
            {exportFormats.includes('sql') && <>
              <DropdownMenuSeparator />
              <SkipIdentityToggleRow testId="table-explorer-export-skip-identity-toggle" />
            </>}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Chart button */}
      {onChart && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onChart}
          disabled={isLoading || !canChart}
          className="h-7 px-2"
          title={canChart ? 'Create chart from data' : 'No data to chart'}
          data-testid="table-explorer-chart-button"
        >
          <BarChart2 className="h-4 w-4" />
        </Button>
      )}

      {/* Pin to dashboard (S20) */}
      {onPinToDashboard && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onPinToDashboard}
          disabled={isLoading}
          className="h-7 px-2"
          title="Pin to dashboard"
          data-testid="table-explorer-pin-dashboard-button"
        >
          <LayoutDashboard className="h-4 w-4" />
        </Button>
      )}

      {/* Row side panel button */}
      {onToggleRowSidePanel && (
        <Button
          variant={isRowSidePanelOpen ? 'secondary' : 'ghost'}
          size="sm"
          onClick={onToggleRowSidePanel}
          disabled={isLoading || !canOpenRowSidePanel}
          className="h-7 px-2"
          title={
            canOpenRowSidePanel
              ? 'Row side panel'
              : isEditMode
                ? 'Row details are unavailable in edit mode'
                : 'No rows loaded'
          }
          data-testid="table-explorer-row-side-panel-button"
        >
          {isRowSidePanelOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
        </Button>
      )}

      {/* Spatial map pane toggle — only when the table has a spatial column */}
      {onToggleMapPane && hasSpatialColumn && (
        <Button
          variant={isMapPaneOpen ? 'secondary' : 'ghost'}
          size="sm"
          onClick={onToggleMapPane}
          disabled={isLoading}
          className="h-7 px-2"
          title={isLoading ? 'Not available while loading' : isMapPaneOpen ? 'Hide map' : 'Show map'}
          data-testid="table-explorer-map-button"
        >
          <Map className="h-4 w-4" />
        </Button>
      )}

      {/* Column profiling toggle */}
      {onToggleColumnProfiling && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onToggleColumnProfiling}
              className={`p-1.5 rounded-md transition-colors ${!isLoading && canColumnProfiling ? 'text-muted-foreground hover:text-foreground hover:bg-background/50' : 'text-muted-foreground/40 cursor-not-allowed'} ${showColumnProfiling ? 'bg-background text-foreground shadow-sm' : ''}`}
              disabled={isLoading || !canColumnProfiling}
              data-testid="table-explorer-column-profiling-toggle"
            >
              <BarChart3 className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {isLoading ? 'Not available while loading' : 'Column profiling — click any column’s bar for histogram and top values'}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Refresh button — re-queries the live DB; hidden in blueprint-edit
          mode where rows come from the sidecar, not a database. */}
      {!blueprintMode && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={isFetching}
          className="h-7 px-2"
          title="Refresh data"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      )}

      {/* Separator */}
      <div className="w-px h-5 bg-border" />

      {/* Edit Mode Controls */}
      {isEditMode ? (
        <>
          {/* Add Row button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddRow}
            disabled={isLoading}
            className="h-7 px-2 text-xs"
            title="Add new row"
            data-testid="add-row-button"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Row
          </Button>

          {/* Save Changes button. In blueprint-edit mode the label makes clear
              edits write to the blueprint files (sidecar + .data.sql), not a
              live database. */}
          <Button
            variant={pendingChangeCount > 0 ? 'default' : 'ghost'}
            size="sm"
            onClick={onSaveChanges}
            disabled={isLoading || pendingChangeCount === 0}
            className="h-7 px-2 text-xs tabular-nums"
            title={
              blueprintMode
                ? `Save ${pendingChangeCount} change${pendingChangeCount !== 1 ? 's' : ''} to the blueprint files`
                : `Save ${pendingChangeCount} pending change${pendingChangeCount !== 1 ? 's' : ''}`
            }
            data-testid="save-changes-button"
          >
            <Save className="h-4 w-4 mr-1" />
            {blueprintMode ? 'Save to Blueprint' : 'Save'}
            {pendingChangeCount > 0 && ` (${pendingChangeCount})`}
          </Button>

          {/* Discard Changes button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onDiscardChanges}
            disabled={isLoading || pendingChangeCount === 0}
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            title="Discard all changes"
            data-testid="discard-changes-button"
          >
            <Undo2 className="h-4 w-4 mr-1" />
            Discard
          </Button>

          {/* Exit Edit Mode button */}
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleEditMode}
            className="h-7 px-2 text-xs"
            title="Exit edit mode"
            data-testid="exit-edit-mode-button"
          >
            <Pencil className="h-4 w-4 mr-1" />
            Edit Mode: ON
          </Button>
        </>
      ) : (
        /* Enable Edit Mode button */
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={hasPrimaryKey && !isLoading && !editDisabledReason ? onToggleEditMode : undefined}
              className={`h-7 px-2 text-xs ${isLoading || !hasPrimaryKey || editDisabledReason ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="edit-mode-button"
              data-disabled-reason={editDisabledKey ?? (hasPrimaryKey ? undefined : 'no-pk')}
            >
              <Pencil className="h-4 w-4 mr-1" />
              Edit Mode
            </Button>
          </TooltipTrigger>
          {/* N24 (C2): below the button, like every toolbar tooltip — above it the tip covered the tab strip. */}
          <TooltipContent side="bottom">
            {editDisabledReason
              ? editDisabledReason
              : isLoading
                ? 'Loading table metadata...'
                : hasPrimaryKey
                  ? 'Enable edit mode to insert, update, or delete rows'
                  : 'Edit mode requires a primary key to uniquely identify rows'}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

// Inline column selector button for adding filters/sorts
type AddColumnButtonProps = {
  type: 'filter' | 'sort';
  columns: TableExplorerColumn[];
  disabledColumns: string[];
  onSelect: (columnName: string) => void;
  disabled?: boolean;
  /** When true, render a plain button (no Popover) that fires onBlockedClick.
   * Used to enforce the "confirm or cancel the dirty filter draft first"
   * rule for the filter picker; a plain Button strips out the Radix machinery
   * entirely so there's no onOpenToggle to fight with. */
  blocked?: boolean;
  onBlockedClick?: () => void;
  label?: string;
  /** Unavailable with this reason (C2 N9): a focusable ReasonButton, no picker. */
  reason?: string;
};

function AddColumnButton({
  type,
  columns,
  disabledColumns,
  onSelect,
  disabled = false,
  blocked = false,
  onBlockedClick,
  label: labelOverride,
  reason,
}: AddColumnButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const Icon = type === 'filter' ? Filter : ArrowUpDown;
  const label = labelOverride ?? (type === 'filter' ? 'Add Filter' : 'Add Sort');

  // Filter columns by search and exclude already used ones
  const selectableColumns = useMemo(() => {
    const searchLower = search.toLowerCase();
    const disabledSet = new Set(disabledColumns.map(normalizeColumnName));

    return columns.filter((col) => {
      if (disabledSet.has(normalizeColumnName(col.name))) return false;
      if (search && !col.name.toLowerCase().includes(searchLower)) return false;
      return true;
    });
  }, [columns, disabledColumns, search]);

  // Reset state when popover opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Scroll selected into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const el = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (columnName: string) => {
      onSelect(columnName);
      setIsOpen(false);
    },
    [onSelect]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (selectableColumns.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < selectableColumns.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < selectableColumns.length) {
            handleSelect(selectableColumns[selectedIndex].name);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          break;
      }
    },
    [selectableColumns, selectedIndex, handleSelect]
  );

  if (reason) {
    return (
      <ReasonButton variant="ghost" size="sm" className="h-7 px-2 text-xs" reason={reason} data-testid={`toolbar-add-${type}`}>
        <Icon className="h-3.5 w-3.5 mr-1" />
        {label}
      </ReasonButton>
    );
  }

  // While blocked, render a plain Button — no Popover at all — so clicking
  // can't open any picker. The handler signals the wizard to pulse instead.
  if (blocked) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs"
        disabled={disabled}
        onClick={onBlockedClick}
        data-testid={`toolbar-add-${type}`}
        data-blocked="true"
      >
        <Icon className="h-3.5 w-3.5 mr-1" />
        {label}
      </Button>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={disabled}
          data-testid={`toolbar-add-${type}`}
        >
          <Icon className="h-3.5 w-3.5 mr-1" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="flex flex-col gap-2">
          <div className="text-xs font-medium text-muted-foreground">
            Select column to {type}
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search columns..."
              className="h-7 pl-7 text-xs"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto" ref={listRef}>
            {selectableColumns.length === 0 ? (
              <div className="text-xs text-muted-foreground py-2 text-center">
                {columns.length === 0 ? 'No columns available' : 'No matching columns'}
              </div>
            ) : (
              selectableColumns.map((col, index) => (
                <button
                  key={col.name}
                  data-index={index}
                  onClick={() => handleSelect(col.name)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded cursor-pointer ${
                    index === selectedIndex ? 'bg-accent' : 'hover:bg-accent'
                  }`}
                >
                  <span className="font-medium">{col.name}</span>
                  <span className="text-muted-foreground text-[10px]">
                    {col.dataType}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
